/**
 * useViewport Hook
 * Returns current viewport type (mobile, compact, or web) and loaded state
 * Returns null viewport until media queries resolve after hydration
 */

'use client';

import { useMemo } from 'react';
import { Viewport, mediaQueries } from '@/theme/breakpoints';
import { useMediaQuery } from './use-media-query';

export interface ViewportState {
  viewport: Viewport | null;
  loaded: boolean;
}

/**
 * Hook to get current viewport type and loaded state
 * @returns { viewport: 'mobile' | 'compact' | 'web' | null, loaded: boolean }
 */
export function useViewport(): ViewportState {
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const isWeb = useMediaQuery(mediaQueries.web);
  const isCompact = useMediaQuery(mediaQueries.compact);
  const isMobile = useMediaQuery(mediaQueries.mobile);

  const viewport = useMemo<Viewport | null>(() => {
    if (isPortrait) return Viewport.Mobile;
    if (isWeb) return Viewport.Web;
    if (isCompact) return Viewport.Compact;
    if (isMobile) return Viewport.Mobile;
    return null;
  }, [isCompact, isMobile, isPortrait, isWeb]);

  const loaded = viewport !== null;

  return { viewport, loaded };
}
